// Auto graphics-tuning. Weak machines (e.g. an Intel-iGPU laptop) get their fans pegged by the
// continuously-rendering overworld + 3D duel board. This picks a starting tier from the device, then
// watches the live frame rate and steps the tier DOWN if the page can't keep up (down-only within a
// session, so it never oscillates; thermal throttling over time trips it too). Only active while the
// player leaves render quality on "Auto" — a manual tier pins it and this stops adjusting.

import { settings, setAutoTier, type QualityTier } from "./stores/settings.svelte.ts";

/** Initial guess from cheap, synchronous device signals. Conservative: never auto-starts "high"
 *  (that's an explicit opt-in). 4-or-fewer cores / ≤4GB RAM reads as a weak machine. */
export function initialTier(
  cores: number = navigator.hardwareConcurrency,
  mem: number = (navigator as unknown as { deviceMemory?: number }).deviceMemory ?? 8,
): QualityTier {
  return (cores || 8) <= 4 || (mem || 8) <= 4 ? "low" : "medium";
}

/** One rung down. Low is the floor. */
export const stepDown = (t: QualityTier): QualityTier => (t === "high" ? "medium" : "low");

/** Below this sustained frame rate the machine is judged to be struggling. Vsync-capped displays sit
 *  at ~60; a page that can't hold the mid-40s under our own 30/60 render caps is genuinely saturated. */
const STRUGGLE_FPS = 45;

let started = false;

export function initPerf(): void {
  if (started) return;
  started = true;
  setAutoTier(initialTier());
  // WebGPU adapter identity is async; integrated Intel graphics is the classic weak-laptop case, so
  // bias to Low the moment we can confirm it (the FPS monitor is the real safety net regardless).
  const gpu = (navigator as unknown as { gpu?: { requestAdapter(): Promise<{ info?: { vendor?: string } } | null> } }).gpu;
  gpu?.requestAdapter().then((a) => {
    const vendor = (a?.info?.vendor ?? "").toLowerCase();
    if (vendor.includes("intel") && settings.renderQuality === "auto" && settings.autoTier === "medium") setAutoTier("low");
  }).catch(() => {});
  startFpsMonitor();
}

function startFpsMonitor(): void {
  let last = performance.now();
  let acc = 0, frames = 0, badSeconds = 0;
  const tick = (now: number): void => {
    requestAnimationFrame(tick);
    const dt = now - last;
    last = now;
    if (dt <= 0 || dt > 1000) return; // first frame / backgrounded tab — don't count the gap
    acc += dt;
    frames++;
    if (acc < 1000) return; // evaluate roughly once a second
    const fps = (frames * 1000) / acc;
    acc = 0;
    frames = 0;
    if (settings.renderQuality !== "auto") { badSeconds = 0; return; }
    if (fps < STRUGGLE_FPS && settings.autoTier !== "low") {
      // Two consecutive bad seconds → step down (one hitch/GC pause shouldn't downgrade the session).
      if (++badSeconds >= 2) { setAutoTier(stepDown(settings.autoTier)); badSeconds = 0; }
    } else {
      badSeconds = 0;
    }
  };
  requestAnimationFrame(tick);
}
